import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addAlert } from 'src/redux/slices/Compliance/CertificationAlertsSlice';
import {
  addHighlight,
  addFsrHighlight,
  addHouseholdConfirmed,
  addHouseholdTransferred,
  setHouseholdCompositionConfirmed,
  setUnconfirmedHouseholds,
  TransferredEventData,
  updateHouseholdMemberInCertifications,
  updateHouseholdName,
  upsertAnswer,
  removeAnswer,
  addFileToAnswer,
  removeFileFromAnswer,
  addSurveyToCertification,
  removeSurveyFromCertification,
  upsertFormSignatureRequest,
  removeFormSignatureRequestByDocId,
  adjustSignatureCount,
  setRemindersSentToday,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { RootState } from 'src/redux/rootReducerType';
import { Certification, CertMember } from 'src/types/Certification';
import CertificationWebSocket from 'src/pages/TenantV2/CertificationWebSocket';
import { certKindLabel } from 'src/components/NewRenterInterface/utils/certTerminology';
import type {
  QuestionnaireAnswer,
  QuestionnaireAnswerFile,
} from 'src/services/RenterQuestionnaire.service';

function mapWsPayloadToCertMember(data: Record<string, unknown>): CertMember {
  return {
    cert_member_id: data.id as number,
    user_id: (data.user_id as number) ?? null,
    user_household_member_id: data.user_household_member_id as number,
    is_head_of_household: data.is_head_of_household as boolean,
    is_helper: data.is_helper as boolean,
    member_type_id: data.member_type_id as number,
    member_type_name: (data.member_type_name as string) || '',
    member_type_weight: (data.member_type_weight as number) ?? null,
    first_name: (data.first_name as string) || '',
    middle_name: (data.middle_name as string) ?? null,
    last_name: (data.last_name as string) || '',
    emails: ((data.emails as Array<Record<string, unknown>>) || []).map(
      (e) => ({
        id: e.id as number,
        email: e.email as string,
        is_primary: e.is_primary as boolean,
      })
    ),
    phones: ((data.phones as Array<Record<string, unknown>>) || []).map(
      (p) => ({
        id: p.id as number,
        phone_number: p.phone_number as string,
        is_primary: p.is_primary as boolean,
      })
    ),
    surveys: (data.surveys as number[]) || [],
  };
}

function isRenterSignature(sig: {
  compliance?: boolean;
  is_date?: boolean;
  is_name?: boolean;
  is_hhmt?: boolean;
  is_phone?: boolean;
}): boolean {
  return (
    !sig.compliance &&
    !sig.is_date &&
    !sig.is_name &&
    !sig.is_hhmt &&
    !sig.is_phone
  );
}

const CertificationUpdateManager: FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];
  const certificationsRef = useRef(certifications);
  certificationsRef.current = certifications;
  const certificationIds = useSelector(
    (state: RootState) => state.newRenterInterface.certificationIds
  ) as number[];

  // Memoize the websocket components to prevent re-creation on parent re-renders
  const websocketConnections = useMemo(
    () =>
      (certificationIds || []).map((certId) => (
        <CertificationWebSocket key={certId} certificationID={certId} />
      )),
    [certificationIds]
  );

  const getPropertyName = useCallback((certificationId: number): string => {
    const cert = certificationsRef.current?.find(
      (c) => c.id === certificationId
    );
    return cert?.property?.name || 'Property';
  }, []);

  const dispatchMemberHighlight = useCallback(
    (member: CertMember): void => {
      dispatch(addHighlight(member.cert_member_id));
    },
    [dispatch]
  );

  // --- v2 WS event handlers ---

  const handleCertMemberAdded = useCallback(
    (data: Record<string, unknown>): void => {
      if (!data?.certification_id) {
        return;
      }
      const certMember = mapWsPayloadToCertMember(data);
      dispatch(
        updateHouseholdMemberInCertifications({
          certificationId: data.certification_id as number,
          householdMember: certMember,
          action: 'add',
        })
      );
      dispatchMemberHighlight(certMember);
      dispatch(
        addAlert({
          context: 'renter',
          alert: {
            type: 'general',
            severity: 'success',
            message: `${
              [certMember.first_name, certMember.last_name]
                .filter(Boolean)
                .join(' ') || 'Household member'
            } added to the ${getPropertyName(
              data.certification_id as number
            )} household`,
            timeout: 7500,
          },
        })
      );
    },
    [dispatch, dispatchMemberHighlight, getPropertyName]
  );

  const handleCertMemberUpdated = useCallback(
    (data: Record<string, unknown>): void => {
      if (!data?.certification_id) {
        return;
      }
      const certMember = mapWsPayloadToCertMember(data);
      dispatch(
        updateHouseholdMemberInCertifications({
          certificationId: data.certification_id as number,
          householdMember: certMember,
          action: 'update',
        })
      );
      dispatch(
        addAlert({
          context: 'renter',
          alert: {
            type: 'general',
            severity: 'success',
            message: `${
              [certMember.first_name, certMember.last_name]
                .filter(Boolean)
                .join(' ') || 'Household member'
            } updated successfully`,
            timeout: 5000,
          },
        })
      );

      if (certMember.is_head_of_household) {
        const newName =
          `${certMember.first_name} ${certMember.last_name}`.trim();
        if (newName) {
          dispatch(
            updateHouseholdName({
              certificationId: data.certification_id as number,
              name: newName,
            })
          );
        }
      }
    },
    [dispatch]
  );

  const handleCertMemberRemoved = useCallback(
    (data: Record<string, unknown>): void => {
      if (!data?.certification_member_id || !data?.certification_id) {
        return;
      }
      const certMember: CertMember = {
        cert_member_id: data.certification_member_id as number,
        user_id: null,
        user_household_member_id: 0,
        is_head_of_household: false,
        is_helper: false,
        member_type_id: 0,
        member_type_name: '',
        member_type_weight: null,
        first_name: '',
        middle_name: null,
        last_name: '',
        emails: [],
        phones: [],
        surveys: [],
      };
      dispatch(
        updateHouseholdMemberInCertifications({
          certificationId: data.certification_id as number,
          householdMember: certMember,
          action: 'remove',
        })
      );
      dispatch(
        addAlert({
          context: 'renter',
          alert: {
            type: 'general',
            severity: 'success',
            message: 'Household member removed from household',
            timeout: 5000,
          },
        })
      );
    },
    [dispatch]
  );

  // --- Legacy WS event handlers (household_transferred / household_composition_confirmed) ---

  const handleHouseholdTransferred = useCallback(
    (data: TransferredEventData): void => {
      const { certification_id } = data;

      dispatch(addHouseholdTransferred(data));

      if (user?.id) {
        dispatch(setUnconfirmedHouseholds(user.id));
      }

      dispatch(
        addAlert({
          context: 'renter',
          alert: {
            type: 'general',
            severity: 'success',
            message: `${getPropertyName(
              certification_id
            )} household was transferred successfully`,
            timeout: 7500,
          },
        })
      );
    },
    [dispatch, user?.id, getPropertyName]
  );

  const handleHouseholdCompositionConfirmed = useCallback(
    (data: { certification_id: number }): void => {
      const certificationId = data?.certification_id;
      if (!certificationId || !user?.id) {
        return;
      }

      dispatch(addHouseholdConfirmed(certificationId));
      dispatch(
        setHouseholdCompositionConfirmed({ certificationId, userId: user.id })
      );
    },
    [dispatch, user?.id]
  );

  const handleSurveyAnswerUpdate = useCallback(
    (data: {
      update_type: string;
      survey_data: Record<string, unknown>;
      certification_id: number;
    }): void => {
      const { update_type, survey_data, certification_id } = data;
      if (!survey_data || !certification_id) {
        return;
      }

      if (update_type === 'deleted') {
        dispatch(
          removeAnswer({
            certificationId: certification_id,
            qitId: survey_data.qit_id as number,
            surveyId: survey_data.survey_id as number,
            answerGroupId: survey_data.answer_group_id as number,
          })
        );
      } else {
        dispatch(
          upsertAnswer({
            certificationId: certification_id,
            answer: survey_data as unknown as QuestionnaireAnswer,
          })
        );
      }
    },
    [dispatch]
  );

  const handleSurveyAdded = useCallback(
    (data: {
      survey: {
        id: number;
        certification_id: number;
        cert_member_id: number | null;
      };
      certification_id: number;
    }): void => {
      if (!data?.survey || !data.certification_id) {
        return;
      }
      dispatch(
        addSurveyToCertification({
          certificationId: data.certification_id,
          survey: data.survey,
        })
      );
    },
    [dispatch]
  );

  const handleSurveyRemoved = useCallback(
    (data: { survey: { id: number }; certification_id: number }): void => {
      if (!data?.survey || !data.certification_id) {
        return;
      }
      dispatch(
        removeSurveyFromCertification({
          certificationId: data.certification_id,
          surveyId: data.survey.id,
        })
      );
    },
    [dispatch]
  );

  const handleFileUpload = useCallback(
    (data: {
      qit_id: number;
      survey_id: number;
      answer_group_id: number;
      file: Record<string, unknown>;
      certification_id: number;
    }): void => {
      if (!data?.file || !data.certification_id) {
        return;
      }
      dispatch(
        addFileToAnswer({
          certificationId: data.certification_id,
          qitId: data.qit_id,
          surveyId: data.survey_id,
          answerGroupId: data.answer_group_id,
          file: data.file as unknown as QuestionnaireAnswerFile,
        })
      );
    },
    [dispatch]
  );

  const handleFileDelete = useCallback(
    (data: {
      qit_id: number;
      survey_id: number;
      answer_group_id: number;
      file_id: number;
      file: { answer_was_deleted?: boolean };
      certification_id: number;
    }): void => {
      if (!data?.certification_id) {
        return;
      }
      dispatch(
        removeFileFromAnswer({
          certificationId: data.certification_id,
          qitId: data.qit_id,
          surveyId: data.survey_id,
          answerGroupId: data.answer_group_id,
          fileId: data.file_id,
        })
      );
      if (data.file?.answer_was_deleted) {
        dispatch(
          removeAnswer({
            certificationId: data.certification_id,
            qitId: data.qit_id,
            surveyId: data.survey_id,
            answerGroupId: data.answer_group_id,
          })
        );
      }
    },
    [dispatch]
  );

  const handleSignaturesChanged = useCallback(
    (data: {
      certification_id?: number;
      documents?: Array<{
        id?: number;
        form_signature_request?: {
          exists?: boolean;
          id?: number;
          requested?: boolean;
          request_date?: string | null;
          document_title?: string | null;
          certification_document_id?: number | null;
          signed_form?: string | null;
          generated_form_url?: string | null;
          needed_signatures?: Array<{
            id: number;
            signature_completed: boolean;
            compliance: boolean;
            household_member: {
              id: number;
              user: number;
              first_name: string;
              last_name: string;
            } | null;
          }>;
        };
      }>;
    }): void => {
      const docs = data?.documents;
      if (!Array.isArray(docs) || !data.certification_id) {
        return;
      }

      const cert = certificationsRef.current?.find(
        (c) => c.id === data.certification_id
      );
      const certMembers = cert?.cert_household?.cert_members ?? [];

      const resolveCmId = (userId: number): number | undefined => {
        return certMembers.find((m) => m.user_id === userId)?.cert_member_id;
      };

      const propertyName = cert?.property?.name ?? '';
      const certKindText = cert
        ? certKindLabel(cert, { capitalize: true })
        : '';
      const effectiveYear = cert?.effective_date
        ? new Date(cert.effective_date).getFullYear().toString()
        : '';
      const certLabel = [certKindText, effectiveYear].filter(Boolean).join(' ');

      for (const doc of docs) {
        const fsr = doc.form_signature_request;
        if (!fsr || !doc.id || !data.certification_id) {
          continue;
        }

        const certId = data.certification_id;

        const prevFsr = certificationsRef.current
          ?.find((c) => c.id === certId)
          ?.form_signatures_request?.find(
            (f) => f.id === fsr.id || f.certification_document === doc.id
          );

        if (!fsr.exists) {
          const adjustedMembers = new Set<number>();
          for (const sig of prevFsr?.needed_signatures ?? []) {
            if (!isRenterSignature(sig)) {
              continue;
            }
            if (!sig.signature_completed) {
              const cmId =
                sig.certification_member_id ??
                resolveCmId(sig.household_member?.user ?? 0);
              if (cmId && !adjustedMembers.has(cmId)) {
                adjustedMembers.add(cmId);
                dispatch(adjustSignatureCount({ certId, cmId, delta: -1 }));
              }
            }
          }
          dispatch(removeFormSignatureRequestByDocId({ certDocId: doc.id }));
          continue;
        }

        if (!fsr.id) {
          continue;
        }

        const wasAlreadyRequested = prevFsr?.requested ?? false;

        dispatch(
          upsertFormSignatureRequest({
            certificationId: certId,
            certDocId: doc.id,
            fsr: {
              id: fsr.id,
              requested: fsr.requested ?? false,
              request_date: fsr.request_date ?? null,
              document_title: fsr.document_title ?? null,
              signed_form: fsr.signed_form ?? null,
              generated_form_url: fsr.generated_form_url ?? null,
              needed_signatures: fsr.needed_signatures ?? [],
            },
          })
        );
        dispatch(addFsrHighlight(fsr.id));

        if (fsr.requested && !wasAlreadyRequested) {
          const docName = fsr.document_title || 'Document';
          dispatch(
            addAlert({
              context: 'renter',
              alert: {
                type: 'general',
                severity: 'info',
                message: certLabel
                  ? `Signature requested: ${docName} (${propertyName}, ${certLabel})`
                  : `Signature requested: ${docName} (${propertyName})`,
                timeout: 7500,
              },
            })
          );
        }

        const currNeeded = fsr.needed_signatures ?? [];
        const prevNeeded = prevFsr?.needed_signatures ?? [];
        const memberIds = new Set<number>();
        for (const sig of currNeeded) {
          const cmId = resolveCmId(sig.household_member?.user ?? 0);
          if (cmId) {
            memberIds.add(cmId);
          }
        }
        for (const sig of prevNeeded) {
          const cmId = resolveCmId(sig.household_member?.user ?? 0);
          if (cmId) {
            memberIds.add(cmId);
          }
        }

        for (const cmId of memberIds) {
          const prevUnsigned = prevNeeded.some(
            (s) =>
              isRenterSignature(s) &&
              !s.signature_completed &&
              resolveCmId(s.household_member?.user ?? 0) === cmId
          );
          const currUnsigned = currNeeded.some(
            (s) =>
              isRenterSignature(s) &&
              !s.signature_completed &&
              resolveCmId(s.household_member?.user ?? 0) === cmId
          );

          if (prevUnsigned !== currUnsigned) {
            dispatch(
              adjustSignatureCount({
                certId,
                cmId,
                delta: currUnsigned ? 1 : -1,
              })
            );
          }
        }
      }
    },
    [dispatch]
  );

  const handleSignatureRemindersSent = useCallback(
    (data: { certification_id: number }): void => {
      if (!data?.certification_id) {
        return;
      }
      dispatch(setRemindersSentToday(data.certification_id));
    },
    [dispatch]
  );

  const onWebSocketEvent = useCallback(
    (event: CustomEvent): void => {
      const { data, type } = event.detail as { data: never; type: string };

      switch (type) {
        case 'cert_member_added':
          handleCertMemberAdded(data as unknown as Record<string, unknown>);
          break;
        case 'cert_member_updated':
          handleCertMemberUpdated(data as unknown as Record<string, unknown>);
          break;
        case 'cert_member_removed':
          handleCertMemberRemoved(data as unknown as Record<string, unknown>);
          break;
        case 'household_transferred':
          handleHouseholdTransferred(data as TransferredEventData);
          break;
        case 'household_composition_confirmed':
          handleHouseholdCompositionConfirmed(data);
          break;
        case 'survey_answer_update':
          handleSurveyAnswerUpdate(data);
          break;
        case 'survey_added':
          handleSurveyAdded(data);
          break;
        case 'survey_removed':
          handleSurveyRemoved(data);
          break;
        case 'file_upload':
          handleFileUpload(data);
          break;
        case 'file_delete':
          handleFileDelete(data);
          break;
        case 'fm_signatures_changed':
          handleSignaturesChanged(data);
          break;
        case 'signature_reminders_sent':
          handleSignatureRemindersSent(data);
          break;
      }
    },
    [
      handleCertMemberAdded,
      handleCertMemberUpdated,
      handleCertMemberRemoved,
      handleHouseholdTransferred,
      handleHouseholdCompositionConfirmed,
      handleSurveyAnswerUpdate,
      handleSurveyAdded,
      handleSurveyRemoved,
      handleFileUpload,
      handleFileDelete,
      handleSignaturesChanged,
      handleSignatureRemindersSent,
    ]
  );

  useEffect(() => {
    document.addEventListener('CertificationWebSocketEvent', onWebSocketEvent);
    return () => {
      document.removeEventListener(
        'CertificationWebSocketEvent',
        onWebSocketEvent
      );
    };
  }, [onWebSocketEvent]);

  return websocketConnections;
};

export default CertificationUpdateManager;
